import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import {
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    Divider,
    AppBar,
    Toolbar,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Menu
} from '@mui/material';
import {
    Menu as MenuIcon,
    Home as HomeIcon,
    CloudUpload as UploadIcon,
    Logout as LogoutIcon,
    Close as CloseIcon,
    MenuBook as MenuBookIcon,
    Search as SearchIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import { hasPermission, Permissions } from '../services/authService';

const Navigation = ({ searchTerm, setSearchTerm, filter, setFilter, user, userRole }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const location = useLocation();

    const handleSignOut = () => {
        auth.signOut();
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Separate menu items into public and protected
    const publicMenuItems = [
        { text: 'Home', icon: <HomeIcon />, path: '/' },
        { text: 'Field Guide', icon: <MenuBookIcon />, path: '/field-guide' },
    ];

    const protectedMenuItems = [
        { text: 'Upload Photos', icon: <UploadIcon />, path: '/upload' }
    ];

    // Add admin menu items
    const adminMenuItems = [
        { text: 'Admin Dashboard', icon: <SettingsIcon />, path: '/admin' }
    ];

    return (
        <>
            <AppBar position="fixed" color="default" elevation={1}>
                <Toolbar>
                    <IconButton
                        component={Link}
                        to="/"
                        edge="start"
                        sx={{ mr: 2 }}
                    >
                        <HomeIcon />
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    {location.pathname === '/' && (
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2,
                            alignItems: 'center',
                            mr: 2
                        }}>
                            <TextField
                                placeholder="Search albums..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                variant="outlined"
                                size="small"
                                sx={{ minWidth: 200 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    label="Sort By"
                                >
                                    <MenuItem value="newest">Newest First</MenuItem>
                                    <MenuItem value="oldest">Oldest First</MenuItem>
                                    <MenuItem value="alphabetical">Alphabetical</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    <IconButton
                        edge="end"
                        onClick={handleMenu}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                {/* Add user info at the top of menu when logged in */}
                {user && (
                    <>
                        <MenuItem disabled sx={{ color: 'text.secondary' }}>
                            <Box>
                                <Typography variant="body2">{user.email}</Typography>
                                <Typography variant="caption" color="primary">
                                    Role: {userRole || 'Guest'}
                                </Typography>
                            </Box>
                        </MenuItem>
                        <Divider />
                    </>
                )}
                
                {/* Keep all existing menu items */}
                {publicMenuItems.map((item) => (
                    <MenuItem 
                        key={item.text}
                        component={Link}
                        to={item.path}
                        onClick={handleClose}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                    </MenuItem>
                ))}
                
                {user && (
                    <>
                        <Divider />
                        {protectedMenuItems.map((item) => (
                            <MenuItem 
                                key={item.text}
                                component={Link}
                                to={item.path}
                                onClick={handleClose}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </MenuItem>
                        ))}
                    </>
                )}

                {/* Add admin menu items */}
                {userRole === 'admin' && (
                    <>
                        <Divider />
                        {adminMenuItems.map((item) => (
                            <MenuItem 
                                key={item.text}
                                component={Link}
                                to={item.path}
                                onClick={handleClose}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </MenuItem>
                        ))}
                    </>
                )}

                {user && (
                    <>
                        <Divider />
                        <MenuItem onClick={() => { handleSignOut(); handleClose(); }}>
                            <ListItemIcon><LogoutIcon /></ListItemIcon>
                            <ListItemText primary="Sign Out" />
                        </MenuItem>
                    </>
                )}
                
                {!user && (
                    <MenuItem 
                        component={Link}
                        to="/signin"
                        onClick={handleClose}
                    >
                        <ListItemIcon><LogoutIcon /></ListItemIcon>
                        <ListItemText primary="Sign In" />
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default Navigation; 